// MEASUREMENTS page — body measurements with calipered %-fat 3-measurement averaging.
// Ported verbatim from the original app's BODY MEASUREMENTS section.
// Chart.js note (known-bugs #4): #measChartWrap has a fixed CSS height (see
// style/layout.css .card usage inline height:160px in index.html), required alongside
// maintainAspectRatio:false to avoid the infinite-resize-loop bug.
import Chart from 'chart.js/auto';
import { S } from '../state.js';
import { save } from '../lib/sync.js';
import { today, fmtDate, fmtDateShort } from '../lib/dates.js';
import { openModal, closeModal } from '../lib/modal.js';
import { registerPage } from '../lib/router.js';
import { IC } from '../lib/icons.js';

export const MEAS_TYPES = { waist: 'Талия', chest: 'Грудь', hips: 'Бёдра', biceps: 'Бицепс', fat: '% жира' };

function fatInterpretation(pct) {
  if (pct < 6) return { label: 'Экстремально низкий', color: 'var(--red)' };
  if (pct < 14) return { label: 'Атлетический', color: 'var(--accent)' };
  if (pct < 18) return { label: 'Спортивный / фитнес', color: 'var(--accent)' };
  if (pct < 25) return { label: 'Норма', color: 'var(--amber)' };
  return { label: 'Выше нормы', color: 'var(--red)' };
}

let measTab = 'waist';
let measChart = null;

export function renderMeasurements() {
  if (!S.measurements) S.measurements = { waist: [], chest: [], hips: [], biceps: [] };
  if (!S.measurements.fat) S.measurements.fat = [];
  document.getElementById('measTabs').innerHTML = Object.keys(MEAS_TYPES).map(t =>
    `<button class="tab-item ${t === measTab ? 'active' : ''}" onclick="switchMeasTab('${t}')">${MEAS_TYPES[t]}</button>`
  ).join('');

  const isFat = measTab === 'fat';
  const unit = isFat ? '%' : 'см';
  const list = [...(S.measurements[measTab] || [])].sort((a, b) => a.date.localeCompare(b.date));
  if (measChart) { measChart.destroy(); measChart = null; }
  if (list.length < 2) {
    document.getElementById('measChartWrap').style.display = 'none';
  } else {
    document.getElementById('measChartWrap').style.display = '';
    const ctx = document.getElementById('measChart').getContext('2d');
    const dark = S.settings.theme === 'dark';
    const gridColor = dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.04)';
    const tickColor = dark ? '#75736E' : undefined;
    measChart = new Chart(ctx, {
      type: 'line',
      data: { labels: list.map(m => fmtDateShort(m.date)), datasets: [{
        data: list.map(m => m.value), borderColor: '#7C3AED', borderWidth: 2,
        pointBackgroundColor: '#7C3AED', pointRadius: 3, tension: 0.3,
        fill: { target: 'origin', above: 'rgba(124,58,237,.06)' },
      }] },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => c.raw + ' ' + unit } } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 0, color: tickColor } },
          y: { grid: { color: gridColor }, ticks: { font: { size: 9 }, callback: v => v + ' ' + unit, color: tickColor } },
        },
      },
    });
  }

  const el = document.getElementById('measList');
  if (!list.length) {
    el.innerHTML = `<div class="empty" style="padding:20px 0"><div class="empty-icon">${IC.ruler}</div><div class="empty-text">Нет замеров</div><div class="empty-sub">Нажми + чтобы добавить первый</div></div>`;
    return;
  }
  el.innerHTML = [...list].reverse().map(m => {
    const interp = isFat ? fatInterpretation(m.value) : null;
    const readingsLine = (isFat && m.readings && m.readings.length > 1)
      ? `<div class="t-secondary" style="font-size:10px;margin-top:2px">Замеры: ${m.readings.join(', ')} → среднее</div>` : '';
    return `
    <div class="card card-sm" style="margin-bottom:8px;padding:12px 14px">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <div class="t-secondary">${fmtDate(m.date)}</div>
        <div style="display:flex;align-items:center;gap:10px">
          <div class="t-h3 t-num">${m.value} ${unit}</div>
          <button onclick="delMeasurement('${measTab}','${m.date}')" class="dish-btn">${IC.trash}</button>
        </div>
      </div>
      ${interp ? `<div style="font-size:11px;font-weight:600;color:${interp.color};margin-top:4px">${interp.label}</div>` : ''}
      ${readingsLine}
    </div>`;
  }).join('');
}
registerPage('measurements', renderMeasurements);

export function switchMeasTab(t) {
  measTab = t;
  renderMeasurements();
}

let measAddType = 'waist';
export function setMeasType(t, btn) {
  measAddType = t;
  document.querySelectorAll('#measTypeGroup .toggle-btn').forEach(b => b.classList.remove('on'));
  btn.classList.add('on');
  document.getElementById('measSimpleRow').style.display = t === 'fat' ? 'none' : '';
  document.getElementById('measFatRows').style.display = t === 'fat' ? '' : 'none';
}

export function openAddMeasurement() {
  measAddType = measTab;
  document.querySelectorAll('#measTypeGroup .toggle-btn').forEach(b => {
    b.classList.toggle('on', b.dataset.type === measTab);
  });
  document.getElementById('measValInp').value = '';
  document.getElementById('measFat1').value = '';
  document.getElementById('measFat2').value = '';
  document.getElementById('measFat3').value = '';
  document.getElementById('measSimpleRow').style.display = measTab === 'fat' ? 'none' : '';
  document.getElementById('measFatRows').style.display = measTab === 'fat' ? '' : 'none';
  openModal('shAddMeasurement');
  setTimeout(() => document.getElementById(measTab === 'fat' ? 'measFat1' : 'measValInp').focus(), 200);
}

export function saveMeasurement() {
  const td = today();
  if (!S.measurements[measAddType]) S.measurements[measAddType] = [];
  if (measAddType === 'fat') {
    const r1 = parseFloat(document.getElementById('measFat1').value);
    const r2 = parseFloat(document.getElementById('measFat2').value);
    const r3 = parseFloat(document.getElementById('measFat3').value);
    const readings = [r1, r2, r3].filter(r => !isNaN(r) && r > 0);
    if (!readings.length) { document.getElementById('measFat1').style.borderColor = 'var(--red)'; return; }
    const avg = readings.reduce((s, r) => s + r, 0) / readings.length;
    S.measurements.fat = S.measurements.fat.filter(m => m.date !== td);
    S.measurements.fat.push({ date: td, value: Math.round(avg * 10) / 10, readings });
  } else {
    const v = parseFloat(document.getElementById('measValInp').value);
    const inp = document.getElementById('measValInp');
    if (isNaN(v) || v <= 0) { inp.style.borderColor = 'var(--red)'; return; }
    S.measurements[measAddType] = S.measurements[measAddType].filter(m => m.date !== td);
    S.measurements[measAddType].push({ date: td, value: v });
  }
  save(true);
  closeModal('shAddMeasurement');
  measTab = measAddType;
  renderMeasurements();
}

export function delMeasurement(type, date) {
  S.measurements[type] = S.measurements[type].filter(m => m.date !== date);
  save(true);
  renderMeasurements();
}
